import { useEffect, useState } from 'react';
import ApperIcon from '@/components/ApperIcon';
import { ListSkeleton } from '@/components/PageSkeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Empty } from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Busy, ErrorState } from '@/components/kit';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';

/** Debounced copy of a value, for search boxes that drive a query. */
export function useDebounced(value, delay = 300) {
  const [out, setOut] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setOut(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return out;
}

/** A Select over a fixed picklist. Empty value shows the placeholder. */
export function PickSelect({ id, value, onChange, values, labels, placeholder = 'Choose…', className, disabled }) {
  const { t } = useT();
  return (
    <Select value={value ? String(value) : undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={cn('w-full', className)}>
        <SelectValue placeholder={t(placeholder)} />
      </SelectTrigger>
      <SelectContent>
        {values.map((v) => (
          <SelectItem key={String(v)} value={String(v)}>
            {labels?.[v] ?? t(String(v))}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A list filter with an 'all' sentinel (shadcn Select rejects an empty value). */
export function FilterSelect({ value, onChange, values, labels, allLabel, className }) {
  const { t } = useT();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn('w-full sm:w-44', className)} aria-label={t(allLabel)}>
        <SelectValue placeholder={t(allLabel)} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t(allLabel)}</SelectItem>
        {values.map((v) => (
          <SelectItem key={String(v)} value={String(v)}>
            {labels?.[v] ?? t(String(v))}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative w-full sm:max-w-xs">
      <ApperIcon
        name="Search"
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9"
        aria-label={placeholder}
      />
    </div>
  );
}

export function Toolbar({ children }) {
  return <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">{children}</div>;
}

export function SwitchRow({ id, label, hint, checked, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-2.5">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch id={id} checked={!!checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

/** Pill toggle used for terms and connection counts. */
export function Pill({ on, onClick, children, className }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'min-h-10 min-w-10 rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-(--transition-fast)',
        on
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:border-primary/60 hover:text-foreground',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * Loading / error / empty / content for a fetched region.
 * First load is a skeleton; a refetch dims the content and marks it busy.
 */
export function ListBody({ loading, data, error, run, isEmpty, empty, rows = 6, children }) {
  const { t } = useT();
  const firstLoad = loading && data === undefined;
  if (firstLoad) return <ListSkeleton rows={rows} />;
  if (error) return <ErrorState message={error} onRetry={run} />;
  if (isEmpty) {
    return (
      <Empty
        icon={empty?.icon ?? 'Ghost'}
        title={empty?.title ?? t('Nothing here yet')}
        description={empty?.description}
        action={empty?.action}
        actionLabel={empty?.actionLabel}
      />
    );
  }
  return (
    <div aria-busy={loading} className={cn('transition-opacity', loading && 'pointer-events-none opacity-60')}>
      {children}
    </div>
  );
}

/** Shell for a short form in a controlled dialog. The caller owns `open`. */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  saving,
  submitLabel = 'Save',
  busyLabel = 'Saving…',
  wide,
  children,
}) {
  const { t } = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-h-[90svh] max-w-[calc(100vw-2rem)] overflow-y-auto', wide ? 'sm:max-w-2xl' : 'sm:max-w-lg')}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {children}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              <Busy busy={saving} label={t(submitLabel)} busyLabel={t(busyLabel)} />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Stars({ value, size = 16 }) {
  const n = Math.round(Number(value || 0));
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <ApperIcon
          key={i}
          name="Star"
          size={size}
          className={i <= n ? 'fill-primary text-primary' : 'text-muted-foreground/40'}
        />
      ))}
    </span>
  );
}
